import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MonitorPlay } from 'lucide-react';
import { DemoWorkspace } from '../DemoWorkspace';
import { useInvestorReducedMotion } from './useInvestorReducedMotion';

/**
 * JourneyShowcase — the amalgamated storytelling surface.
 *
 * Replaces the old scroll-driven film (useCinematicScenes scroll travel +
 * full-height sticky scenes) that acted as a drop-off center. Instead:
 *   LEFT  = narration chapters (capability + explanation + human checkpoint)
 *   RIGHT = sticky MacBook running the live product surface for that chapter
 *
 * Chapter changes are click-driven; the MacBook never leaves the viewport,
 * so buyers get the narration they need without being bottlenecked by it.
 */
export const JourneyShowcase = ({ film, onSceneChange }) => {
  const reduced = useInvestorReducedMotion();
  const [active, setActive] = useState(0);
  const scene = film.scenes[active];

  const goTo = (index) => {
    setActive(index);
    if (onSceneChange) onSceneChange(index);
  };

  const chooseProductScene = (productIndex) => {
    const next = film.scenes.findIndex((item) => item.productScene === productIndex);
    if (next >= 0) goTo(next);
  };

  return (
    <div className="journey-showcase" data-testid={`${film.id}-showcase`}>
      {/* LEFT — narration chapters: capability + explanation */}
      <div className="journey-chapters" data-testid={`${film.id}-chapter-list`}>
        <p className="journey-kicker">STORY / {film.label}</p>
        <nav aria-label={`${film.label} chapters`} className="journey-chapter-rail">
          {film.scenes.map((item, index) => (
            <button
              key={item.verb}
              type="button"
              className={index === active ? 'active' : index < active ? 'passed' : ''}
              onClick={() => goTo(index)}
              aria-current={index === active ? 'true' : undefined}
              data-testid={`${film.id}-capability-${item.verb.toLowerCase()}-button`}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              <b>{item.verb}</b>
            </button>
          ))}
        </nav>
        <AnimatePresence mode="wait">
          <motion.article
            key={active}
            className="journey-chapter"
            initial={reduced ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: reduced ? 0 : 0.22, ease: [0.215, 0.61, 0.355, 1] }}
            data-testid={`${film.id}-scene-caption`}
          >
            <p>{scene.eyebrow}</p>
            <h2 data-testid={`${film.id}-title`}>{scene.title}</h2>
            <span className="journey-body">{scene.body}</span>
            <div className="journey-action" data-testid={`${film.id}-action-callout`}>
              <strong><MonitorPlay size={12} /> AHI DOES / {scene.verb}</strong>
              <p>{scene.action}</p>
            </div>
            <aside className="journey-human" data-testid={`${film.id}-human-checkpoint`}>
              <span><i /> HUMAN LINE</span>
              <p>{scene.human}</p>
            </aside>
          </motion.article>
        </AnimatePresence>
        <footer className="journey-progress" aria-hidden="true">
          <motion.i animate={{ scaleX: (active + 1) / film.scenes.length }} />
          <em>{String(active + 1).padStart(2, '0')} / {String(film.scenes.length).padStart(2, '0')}</em>
        </footer>
      </div>

      {/* RIGHT — sticky MacBook running the live surface for this chapter */}
      <div className="journey-macbook" data-testid={`${film.id}-laptop-stage`}>
        <div className="journey-macbook-sticky">
          <motion.div
            className="laptop-film-shell journey-shell"
            animate={reduced ? {} : { scale: 0.97 + active * 0.008 }}
            transition={{ duration: reduced ? 0 : 0.26, ease: [0.645, 0.045, 0.355, 1] }}
          >
            <div className="laptop-film-lid" data-testid={`${film.id}-macbook-frame`}>
              <div className="laptop-film-camera" aria-hidden="true" />
              <div className="laptop-film-screen" data-testid={`${film.id}-live-product-screen`}>
                <DemoWorkspace
                  compact
                  showcase
                  scenarioId={film.scenarioId}
                  activeSceneIndex={scene.productScene}
                  onActiveSceneChange={chooseProductScene}
                />
              </div>
            </div>
            <div className="laptop-film-base" aria-hidden="true"><i /></div>
          </motion.div>
          <p className="laptop-modeled-label" data-testid={`${film.id}-modeled-label`}>
            MODELED ENTERPRISE SCENARIO / LIVE PRODUCT SURFACE
          </p>
        </div>
      </div>
    </div>
  );
};
